/**
 * ITER305-refine · benefitFormatter
 *
 * Single source of truth for how a voucher's benefit renders as a
 * headline string. Previously each surface (VoucherCard, LightVoucherPreview,
 * PremiumComponents.benefitHeadline) had its own logic and every one of
 * them prefixed ₹ even for non-monetary benefits like
 * "Two Complimentary Welcome Desserts" — where 2 is a QUANTITY, not
 * a price.
 *
 * Contract:
 *   • Percentage types                    → "20%"
 *   • Monetary types                      → "₹1,500"
 *   • Non-monetary / complimentary types  → "2" (or voucher.name if the
 *                                            value is non-numeric)
 *   • Missing value                       → face_value_label || name || ""
 *
 * Callers can request a longer form via `variant`:
 *   • 'value'   (default)  → "₹1,500"
 *   • 'off'                → "₹1,500 OFF"    (only for discount types)
 *   • 'back'               → "₹150 BACK"     (only for cashback)
 */

// Explicit whitelist beats an inferred `!isNumeric` — new benefit
// types must be classified deliberately.
const MONETARY_TYPES = new Set([
  'flat_discount', 'flat_off',
  'cashback', 'gift_value',
]);
const PERCENT_TYPES = new Set([
  'percent_discount', 'percentage_off', 'percentage', 'percent_off',
]);
const NON_MONETARY_TYPES = new Set([
  'complimentary_item', 'buy_one_get_one', 'free_item', 'bogo',
]);

export function isMonetaryBenefit(voucher) {
  const t = voucher?.benefit_type;
  if (voucher?.show_currency === false) return false;
  if (voucher?.show_currency === true)  return true;
  if (!t) return false;
  return MONETARY_TYPES.has(t);
}

export function formatBenefitValue(voucher, variant = 'value') {
  const raw = voucher?.benefit_value;
  const type = voucher?.benefit_type;

  if (raw == null || raw === '') {
    return voucher?.face_value_label || voucher?.name || '';
  }

  if (PERCENT_TYPES.has(type)) {
    return `${Number(raw)}%${variant === 'off' ? ' OFF' : ''}`;
  }

  const numeric = typeof raw === 'number' || /^\d+(\.\d+)?$/.test(String(raw));

  if (MONETARY_TYPES.has(type) && numeric) {
    const pretty = `₹${Number(raw).toLocaleString('en-IN')}`;
    if (variant === 'off')  return `${pretty} OFF`;
    if (variant === 'back') return `${pretty} BACK`;
    return pretty;
  }

  // Everything else — complimentary items, BOGO, free items, or an
  // unrecognised benefit type — renders the value WITHOUT ₹.
  if (NON_MONETARY_TYPES.has(type)) {
    return String(raw);
  }

  // Legacy fallback: numeric value with no known type. Show as number
  // (no currency prefix) so we never accidentally slap ₹ on a quantity.
  return numeric ? String(Number(raw)) : String(raw);
}
